import { prefixClass } from 'lib/utils';
import { IconLoupe, IconEmailNew, IconAddNew, IconTaskNew, IconFolder } from 'components/Icons';
import KSButton from '../../../../../../Common/KSButton';
import { Dropdown, DropdownItemConfig } from '../../../../../../Common/Dropdown';
import { useAuthContext } from 'context/useAuthContext';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface SearchRowProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onCreateTodo?: () => void;
  onCreateEmail?: () => void;
  onCreateGroup?: () => void;
  activeView?: string;
}

/**
 * SearchRow - Top row of the browser toolbar.
 * Contains search input and create dropdown.
 */
export const SearchRow = ({
  searchQuery = '',
  onSearchChange,
  onCreateTodo,
  onCreateEmail,
  onCreateGroup,
}: SearchRowProps) => {
  const prefix = prefixClass('ksq2-browser-toolbar');
  const { isBookkeeperAdmin } = useAuthContext();
  const { enableCreateQueueEmailModal } = useFlags();
  const createItems: DropdownItemConfig[] = [
    {
      id: 'new-todo',
      label: 'New To-Do',
      icon: <IconTaskNew />,
      onClick: () => onCreateTodo?.(),
    },
    {
      id: 'new-email',
      label: 'New Email',
      icon: <IconEmailNew />,
      onClick: () => onCreateEmail?.(),
    },
    {
      id: 'new-group',
      label: 'New Group',
      icon: <IconFolder />,
      onClick: () => onCreateGroup?.(),
    },
  ];

  const onCreateTodoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCreateTodo?.();
  };

  const dropdownTrigger = () => (
    <button className={prefix('__create-btn')} type="button">
      To-Do
      <span className={prefix('__create-icon')} onClick={onCreateTodoClick} title="Quick create To-Do">
        <IconAddNew />
      </span>
    </button>
  );

  const shouldEnableDropdown = isBookkeeperAdmin && enableCreateQueueEmailModal;

  return (
    <div className={prefix('__search-row')}>
      <div className={prefix('__search')}>
        <div className={prefix('__search-icon')}>
          <IconLoupe />
        </div>
        <input
          type="text"
          placeholder="Search"
          className={prefix('__search-input')}
          value={searchQuery}
          onChange={e => onSearchChange?.(e.target.value)}
          onKeyDown={e => e.key === 'Escape' && onSearchChange?.('')}
        />
        {searchQuery && (
          <KSButton
            variant="secondary"
            onClick={() => onSearchChange?.('')}
            label="Clear"
            tooltip="Clear search"
            className={`${prefix('__clear-btn')}`}
          />
        )}
      </div>
      <div className={prefix('__create')}>
        {shouldEnableDropdown ? (
          <Dropdown
            trigger={dropdownTrigger()}
            items={createItems}
            position="bottom"
            alignment="between"
            itemAlignment="between"
            tooltip="Create new item"
            className={`${prefix('__create-dropdown')}`}
          />
        ) : (
          <button
            className={prefix('__create-btn')}
            type="button"
            onClick={() => onCreateTodo?.()}
            title="Create To-Do"
          >
            To-Do
            <span className={prefix('__create-icon')}>
              <IconAddNew />
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
